import { useState } from 'react';
import { canAssignRowerToSeat } from '../utils/riggingValidation';

/**
 * Hook for drag and drop functionality specifically for the schedule modal.
 * Unlike useDragAndDrop, this doesn't make API calls and only manages local state.
 */
export const useScheduleModalDragAndDrop = (onLineupUpdate) => {
  const [draggedRower, setDraggedRower] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const handleDragStart = (e, rower) => {
    if (!rower) {
      return;
    }

    setDraggedRower(rower);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify(rower));
    e.dataTransfer.setData('text/plain', rower.id.toString());
  };

  const handleDragEnter = (e, seat_number) => {
    e.preventDefault();
    setDropTarget(seat_number);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = (e) => {
    // Only clear drop target if we're actually leaving the drop zone
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropTarget(null);
    }
  };

  const handleDrop = (e, seat_number, crew, currentLineup) => {
    e.preventDefault();
    setDropTarget(null);

    // Get rower data
    let rowerData = draggedRower;

    // If state is null, try to get from dataTransfer
    if (!rowerData) {
      try {
        const jsonData = e.dataTransfer.getData('application/json');
        if (jsonData) {
          rowerData = JSON.parse(jsonData);
        }
      } catch (error) {
        console.error('Failed to parse drag data:', error);
        return;
      }
    }

    if (!rowerData || !crew) {
      console.error('Missing rower data or crew for drop operation');
      return;
    }

    // Validate side preference if dropping to a specific seat
    if (typeof seat_number === 'number' && seat_number >= 0) {
      const validation = canAssignRowerToSeat(crew, rowerData, seat_number);
      if (!validation.canAssign) {
        if (validation.message.includes('Rigging configuration is required')) {
          console.error(`Assignment blocked: ${validation.message}`);
          return; // Block the assignment
        } else {
          console.warn(`Side preference warning: ${validation.message}`);
          // Continue with assignment but log warning
        }
      }
    }

    // Create updated lineup
    const newLineup = [...currentLineup];

    // If seat_number is a valid index, place the rower there
    if (typeof seat_number === 'number' && seat_number >= 0 && seat_number < newLineup.length) {
      // First, remove the rower from their previous position (if any)
      for (let i = 0; i < newLineup.length; i++) {
        if (newLineup[i] && newLineup[i].id === rowerData.id) {
          newLineup[i] = null;
          break;
        }
      }

      // Then add them to the new position
      newLineup[seat_number] = {
        id: rowerData.id,
        name: `${rowerData.first_name} ${rowerData.last_name}`,
        first_name: rowerData.first_name,
        last_name: rowerData.last_name,
        preferred_side: rowerData.preferred_side || rowerData.side_preference,
        availability_status: rowerData.availability_status,
        availability_details: rowerData.availability_details,
      };
    }

    // Call the update function
    onLineupUpdate(crew.id, newLineup);
    setDraggedRower(null);
  };

  const handleRemoveMember = (crew, currentLineup, seatIndex) => {
    const newLineup = [...currentLineup];
    newLineup[seatIndex] = null;
    onLineupUpdate(crew.id, newLineup);
  };

  return {
    dropTarget,
    handleDragStart,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleRemoveMember,
  };
};
